import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  Loader2,
  MapPin,
  Plus,
  Trash2,
  User,
  Video,
  Link as LinkIcon,
  AlertCircle,
} from 'lucide-react';
import { createInterview, fetchTeamMembers, fetchJobs, type TeamMember } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface InterviewSchedulerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  candidateId?: string;
  jobId?: string;
  applicationId?: string;
}

interface Attendee {
  type: 'internal' | 'external';
  userId?: string;
  userName?: string;
  externalName?: string;
  externalEmail?: string;
}

const INTERVIEW_TYPES = [
  { value: 'phone', label: 'Phone Screen' },
  { value: 'video', label: 'Video Call' },
  { value: 'onsite', label: 'On-site' },
  { value: 'panel', label: 'Panel Interview' },
] as const;

const DURATION_OPTIONS = [
  { value: '30', label: '30 minutes' },
  { value: '45', label: '45 minutes' },
  { value: '60', label: '60 minutes' },
  { value: '90', label: '90 minutes' },
] as const;

export function InterviewScheduler({
  open,
  onOpenChange,
  onSuccess,
  candidateId,
  jobId,
  applicationId,
}: InterviewSchedulerProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [type, setType] = useState('video');
  const [scheduledAt, setScheduledAt] = useState('');
  const [duration, setDuration] = useState('60');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [meetingLink, setMeetingLink] = useState('');
  const [selectedJobId, setSelectedJobId] = useState(jobId || '');
  const [error, setError] = useState<string | null>(null);

  // Fetch jobs when no jobId is provided. Paused jobs are included — they are
  // still actively worked (only unpublished from the site), so interviews can
  // still be scheduled for them.
  const { data: jobsData } = useQuery({
    queryKey: ['jobs', { limit: 100 }],
    queryFn: () => fetchJobs({ limit: 100 }),
    enabled: open && !jobId,
  });

  const selectableJobs = (jobsData?.data ?? []).filter(
    (job) => job.status === 'open' || job.status === 'paused',
  );

  const effectiveJobId = jobId || selectedJobId;

  // External attendee form
  const [showAddExternal, setShowAddExternal] = useState(false);
  const [externalName, setExternalName] = useState('');
  const [externalEmail, setExternalEmail] = useState('');

  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team-members'],
    queryFn: fetchTeamMembers,
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: () => {
      if (!candidateId || !effectiveJobId) {
        throw new Error('Candidate and job are required');
      }
      return createInterview({
        candidateId,
        jobId: effectiveJobId,
        applicationId,
        title,
        type,
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMinutes: parseInt(duration, 10),
        location: type === 'onsite' ? location : undefined,
        meetingLink: type === 'video' ? meetingLink : undefined,
        notes: notes || undefined,
        attendees: attendees.map((a) => ({
          userId: a.userId,
          externalName: a.externalName,
          externalEmail: a.externalEmail,
          role: 'interviewer',
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      addToast({ type: 'success', title: 'Interview scheduled successfully' });
      resetForm();
      onOpenChange(false);
      onSuccess();
    },
    onError: (err: Error) => {
      setError(err.message);
      addToast({ type: 'error', title: 'Failed to schedule interview', message: err.message });
    },
  });

  const resetForm = () => {
    setTitle('');
    setType('video');
    setScheduledAt('');
    setDuration('60');
    setLocation('');
    setNotes('');
    setAttendees([]);
    setMeetingLink('');
    setSelectedJobId(jobId || '');
    setError(null);
    setShowAddExternal(false);
    setExternalName('');
    setExternalEmail('');
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  const addInternalAttendee = (userId: string) => {
    const member = teamMembers.find((m: TeamMember) => m.id === userId);
    if (!member) return;
    if (attendees.some((a) => a.userId === userId)) return;
    setAttendees([
      ...attendees,
      {
        type: 'internal',
        userId: member.id,
        userName: `${member.firstName} ${member.lastName}`,
      },
    ]);
  };

  const addExternalAttendee = () => {
    if (!externalName.trim() || !externalEmail.trim()) return;
    setAttendees([
      ...attendees,
      {
        type: 'external',
        externalName: externalName.trim(),
        externalEmail: externalEmail.trim(),
      },
    ]);
    setExternalName('');
    setExternalEmail('');
    setShowAddExternal(false);
  };

  const removeAttendee = (index: number) => {
    setAttendees(attendees.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!scheduledAt) {
      setError('Date and time are required');
      return;
    }
    if (!candidateId || !effectiveJobId) {
      setError('Please select a job for this interview');
      return;
    }

    createMutation.mutate();
  };

  const availableMembers = teamMembers.filter(
    (m: TeamMember) => !attendees.some((a) => a.userId === m.id),
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-emerald-500" />
            Schedule Interview
          </DialogTitle>
          <DialogDescription>
            Set up an interview with the candidate. All attendees will be notified.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Job Selector (when not pre-set) */}
          {!jobId && (
            <div className="space-y-2">
              <Label>Job *</Label>
              <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a job..." />
                </SelectTrigger>
                <SelectContent>
                  {selectableJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title}{job.company ? ` - ${job.company.name}` : ''}
                      {job.status === 'paused' ? ' (Paused)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="interview-title">Title *</Label>
            <Input
              id="interview-title"
              placeholder="e.g. First Round Technical Interview"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Type and Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Interview Type *</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVIEW_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Duration *</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date and Time */}
          <div className="space-y-2">
            <Label htmlFor="interview-datetime">Date & Time *</Label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
              <Input
                id="interview-datetime"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="pl-9"
                required
              />
            </div>
          </div>

          {/* Location (for onsite) */}
          {type === 'onsite' && (
            <div className="space-y-2">
              <Label htmlFor="interview-location">Location</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
                <Input
                  id="interview-location"
                  placeholder="e.g. 123 Main Street, Office 4B"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          )}

          {/* Meeting Link (for video) */}
          {type === 'video' && (
            <div className="space-y-2">
              <Label htmlFor="interview-meeting-link">Meeting Link</Label>
              <div className="relative">
                <Video className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
                <Input
                  id="interview-meeting-link"
                  placeholder="https://meet.google.com/... or https://zoom.us/..."
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                  className="pl-9"
                />
              </div>
              {meetingLink && (
                <div className="flex items-center gap-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-md text-sm text-emerald-400">
                  <LinkIcon className="h-3.5 w-3.5 flex-shrink-0" />
                  <a
                    href={meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate hover:underline"
                  >
                    {meetingLink}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Attendees */}
          <div className="space-y-3">
            <Label>Attendees</Label>

            {/* Current attendees list */}
            {attendees.length > 0 && (
              <div className="space-y-2">
                {attendees.map((attendee, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2.5 bg-bg-secondary rounded-md border border-white/10"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="h-8 w-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <User className="h-4 w-4 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">
                          {attendee.type === 'internal'
                            ? attendee.userName
                            : attendee.externalName}
                        </p>
                        <p className="text-xs text-text-tertiary">
                          {attendee.type === 'internal' ? 'Team member' : attendee.externalEmail}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttendee(index)}
                      className="text-text-tertiary hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add internal attendee */}
            {availableMembers.length > 0 && (
              <div className="space-y-2">
                <Select
                  value=""
                  onValueChange={(val) => {
                    if (val) addInternalAttendee(val);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Add team member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMembers.map((member: TeamMember) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.firstName} {member.lastName} ({member.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Add external attendee */}
            {showAddExternal ? (
              <div className="space-y-3 p-3 bg-bg-secondary rounded-md border border-white/10">
                <p className="text-sm font-medium text-text-secondary">Add External Attendee</p>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Full name"
                    value={externalName}
                    onChange={(e) => setExternalName(e.target.value)}
                  />
                  <Input
                    placeholder="Email address"
                    type="email"
                    value={externalEmail}
                    onChange={(e) => setExternalEmail(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={addExternalAttendee}
                    disabled={!externalName.trim() || !externalEmail.trim()}
                  >
                    Add
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAddExternal(false);
                      setExternalName('');
                      setExternalEmail('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddExternal(true)}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add External Attendee
              </Button>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="interview-notes">Notes</Label>
            <Textarea
              id="interview-notes"
              placeholder="Preparation notes, talking points, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule Interview
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
